import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Kangaroo — a storybook mother kangaroo standing upright on big feet and a
// thick tail, with a joey's head peeking out of her belly pouch. Chunky toy
// proportions: an oversized head with tall ears on a plump tan body, stubby
// arms tucked in front, huge planted feet. Built to animate: the head, the
// ears and the joey are named so the "joey-peek" loop can pop the little one
// up for a look around while mum checks on it, and the idle keeps the pair
// gently bouncing.
//
// Frame: faces +Z, stands on the floor (y = 0). About 1.1 m tall at the ears,
// the tail resting on the ground behind.
//
// Rig layout (flat, no hierarchy — each named node moves about its own pivot):
//   - "body"  : torso, belly, pouch rim, arms and neck. Bounces.
//   - "head"  : the whole face (snout, nose, eyes). Pivot at the neck.
//   - "ear-l" / "ear-r" : the tall ears. Pivot where each meets the head,
//               so flops read as flops, not orbits.
//   - "joey"  : the joey's head, ears and face inside the pouch. Pivot at
//               the pouch mouth, so it rises and ducks straight up and down.
// The feet, haunches and tail are static (unnamed) — the planted tripod.

const FUR = "#c98a4b";         // warm tan fur
const FUR_DARK = "#a56c36";    // shaded tan for haunches, arms and the tail
const CREAM = "#f6efe3";       // belly, pouch and inner ears
const CREAM_SHADE = "#e4dac6"; // the pouch rim, a shade deeper so the lip reads
const JOEY = "#dba86a";        // the joey — a lighter, milkier tan than mum
const NOSE = "#4a3a35";        // soft dark brown noses, never black
const EYE_WHITE = "#faf6ec";   // eye whites
const PUPIL = "#33282e";       // big friendly pupils

// The pivots the animations rotate the named nodes about.
const BODY_PIVOT: [number, number, number] = [0, 0.28, 0];
const NECK_PIVOT: [number, number, number] = [0, 0.78, 0.04];
const EAR_L_PIVOT: [number, number, number] = [-0.08, 0.95, 0.02];
const EAR_R_PIVOT: [number, number, number] = [0.08, 0.95, 0.02];
const POUCH_PIVOT: [number, number, number] = [0, 0.545, 0.15];

// One big friendly eye for mum: white ball, big pupil slightly forward,
// catch-light.
function eye(x: number): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.028, 10, 8), color: EYE_WHITE,
      position: [x, 0.905, 0.14], name: "head" },
    { geometry: new THREE.SphereGeometry(0.018, 8, 6), color: PUPIL,
      position: [x * 0.96, 0.903, 0.159], name: "head" },
    { geometry: new THREE.SphereGeometry(0.006, 6, 5), color: EYE_WHITE,
      position: [x * 0.96 + 0.007, 0.912, 0.172], name: "head" },
  ];
}

// One tall ear: a long tan leaf with a cream lining, tipped slightly outward.
function ear(name: string, x: number): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.06, 10, 8), color: FUR,
      position: [x, 1.03, 0.02], scale: [0.55, 1.6, 0.75],
      rotation: [0, 0, -Math.sign(x) * 0.18], name },
    { geometry: new THREE.SphereGeometry(0.045, 8, 6), color: CREAM,
      position: [x * 1.05, 1.03, 0.048], scale: [0.35, 1.25, 0.5],
      rotation: [0, 0, -Math.sign(x) * 0.18], name },
  ];
}

// One stubby arm: a soft bent limb reaching from the shoulder down to a paw
// resting on the pouch rim.
function arm(x: number): Part[] {
  const s = Math.sign(x);
  return [
    { geometry: new THREE.SphereGeometry(0.045, 9, 7), color: FUR_DARK,
      position: [x, 0.615, 0.075], scale: [0.7, 1.45, 0.7],
      rotation: [-0.55, 0, -s * 0.35], name: "body" },
    { geometry: new THREE.SphereGeometry(0.04, 8, 6), color: FUR_DARK,
      position: [x * 0.82, 0.545, 0.145], scale: [1.0, 0.85, 1.1], name: "body" },
  ];
}

// The joey: a milky-tan head with oversized ears and big eyes, peeking over
// the pouch rim. All named "joey" so it rises and ducks as one.
function joey(): Part[] {
  const joeyEye = (x: number): Part[] => [
    { geometry: new THREE.SphereGeometry(0.016, 8, 6), color: EYE_WHITE,
      position: [x, 0.595, 0.203], name: "joey" },
    { geometry: new THREE.SphereGeometry(0.010, 6, 5), color: PUPIL,
      position: [x * 0.96, 0.594, 0.214], name: "joey" },
  ];
  return [
    // The little head, proud of the pouch rim.
    { geometry: new THREE.SphereGeometry(0.06, 12, 10), color: JOEY,
      position: [0, 0.585, 0.16], scale: [1.0, 1.0, 0.95], name: "joey" },
    // Oversized joey ears — all ears, this one.
    { geometry: new THREE.SphereGeometry(0.028, 8, 6), color: JOEY,
      position: [-0.04, 0.652, 0.145], scale: [0.55, 1.4, 0.65],
      rotation: [0, 0, 0.25], name: "joey" },
    { geometry: new THREE.SphereGeometry(0.028, 8, 6), color: JOEY,
      position: [0.04, 0.652, 0.145], scale: [0.55, 1.4, 0.65],
      rotation: [0, 0, -0.25], name: "joey" },
    ...joeyEye(-0.026),
    ...joeyEye(0.026),
    // A tiny snub nose.
    { geometry: new THREE.SphereGeometry(0.011, 6, 5), color: NOSE,
      position: [0, 0.576, 0.215], name: "joey" },
  ];
}

export const kangaroo = defineModel({
  id: "kangaroo",
  batchId: "mess",
  name: "Kangaroo",
  tags: ["mess", "cute", "animated", "creature", "animal", "kangaroo", "marsupial", "joey", "mascot"],
  summary: "A mother kangaroo on big feet and tail, a joey peeking from her pouch.",
  description:
    "A storybook mother kangaroo standing upright on huge planted feet and a " +
    "thick ground-resting tail: an oversized head with tall cream-lined ears, " +
    "a plump tan body with stubby arms tucked in front, and a milky-tan joey " +
    "head peeking out of her cream belly pouch. The head, each ear and the " +
    "joey are separately posable; the joey-peek loop pops the little one up " +
    "for a look around and ducks it back down while mum checks on it, and the " +
    "idle keeps the pair gently bouncing.",
  author: "models",
  footprint: 0.5,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // ── the planted tripod: feet + tail (static) ───────────────────────
      // Two huge feet reaching forward.
      { geometry: new THREE.SphereGeometry(0.075, 10, 8), color: FUR_DARK,
        position: [-0.105, 0.05, 0.09], scale: [0.85, 0.65, 2.4] },
      { geometry: new THREE.SphereGeometry(0.075, 10, 8), color: FUR_DARK,
        position: [0.105, 0.05, 0.09], scale: [0.85, 0.65, 2.4] },
      // The thick tail, stepping down to rest on the ground behind.
      { geometry: new THREE.SphereGeometry(0.085, 10, 8), color: FUR_DARK,
        position: [0, 0.20, -0.20], scale: [0.9, 0.9, 1.3] },
      { geometry: new THREE.SphereGeometry(0.07, 9, 7), color: FUR_DARK,
        position: [0, 0.13, -0.31], scale: [0.85, 0.85, 1.35] },
      { geometry: new THREE.SphereGeometry(0.055, 8, 6), color: FUR_DARK,
        position: [0, 0.06, -0.41], scale: [0.8, 0.8, 1.4] },
      // Big round haunches over the feet.
      { geometry: new THREE.SphereGeometry(0.125, 11, 9), color: FUR_DARK,
        position: [-0.14, 0.24, -0.03], scale: [0.72, 1.05, 1.1] },
      { geometry: new THREE.SphereGeometry(0.125, 11, 9), color: FUR_DARK,
        position: [0.14, 0.24, -0.03], scale: [0.72, 1.05, 1.1] },
      // Strong lower legs joining the haunches to the heels of the feet.
      { geometry: new THREE.SphereGeometry(0.06, 9, 7), color: FUR_DARK,
        position: [-0.115, 0.12, 0.0], scale: [0.75, 1.5, 0.9] },
      { geometry: new THREE.SphereGeometry(0.06, 9, 7), color: FUR_DARK,
        position: [0.115, 0.12, 0.0], scale: [0.75, 1.5, 0.9] },
      // ── body (bounces) ─────────────────────────────────────────────────
      // Upright plump torso.
      { geometry: new THREE.SphereGeometry(0.165, 12, 10), color: FUR,
        position: [0, 0.52, -0.01], scale: [1.0, 1.45, 0.9], name: "body" },
      // Cream belly carrying the pouch.
      { geometry: new THREE.SphereGeometry(0.125, 11, 9), color: CREAM,
        position: [0, 0.44, 0.09], scale: [1.0, 1.2, 0.75], name: "body" },
      // The pouch rim — a deeper cream lip the joey peeks over.
      { geometry: new THREE.SphereGeometry(0.10, 10, 8), color: CREAM_SHADE,
        position: [0, 0.545, 0.12], scale: [1.05, 0.4, 0.65], name: "body" },
      // Neck joining torso to head.
      { geometry: new THREE.CylinderGeometry(0.055, 0.07, 0.09, 8), color: FUR,
        position: [0, 0.775, 0.03], name: "body" },
      ...arm(-0.135),
      ...arm(0.135),
      // ── head ───────────────────────────────────────────────────────────
      // The oversized round head.
      { geometry: new THREE.SphereGeometry(0.115, 14, 12), color: FUR,
        position: [0, 0.88, 0.05], scale: [0.95, 1.0, 1.0], name: "head" },
      // The long soft snout with a cream chin.
      { geometry: new THREE.SphereGeometry(0.065, 10, 8), color: FUR,
        position: [0, 0.845, 0.155], scale: [0.9, 0.75, 1.2], name: "head" },
      { geometry: new THREE.SphereGeometry(0.045, 8, 6), color: CREAM,
        position: [0, 0.815, 0.16], scale: [0.9, 0.6, 1.0], name: "head" },
      // The soft dark nose at the tip.
      { geometry: new THREE.SphereGeometry(0.028, 8, 6), color: NOSE,
        position: [0, 0.86, 0.225], scale: [1.1, 0.8, 0.7], name: "head" },
      ...eye(-0.055),
      ...eye(0.055),
      // ── the tall ears ──────────────────────────────────────────────────
      ...ear("ear-l", -0.08),
      ...ear("ear-r", 0.08),
      // ── the joey in the pouch ──────────────────────────────────────────
      ...joey(),
    ];
  },
  animations: [
    // 1. Idle — a springy standing bounce: the body dips and rises on its
    //    haunches, the head counter-bobs, the ears flop a beat behind and the
    //    joey rides along in the pouch.
    {
      id: "idle",
      label: "Idle bounce",
      duration: 2.8,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.7, value: [0, -0.022, 0], ease: "sine" },
            { t: 1.4, value: [0, 0.012, 0], ease: "sine" },
            { t: 2.1, value: [0, -0.022, 0], ease: "sine" },
            { t: 2.8, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "head", pivot: NECK_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.7, value: [0, -0.028, 0], ease: "sine" },
            { t: 1.4, value: [0, 0.016, 0], ease: "sine" },
            { t: 2.1, value: [0, -0.028, 0], ease: "sine" },
            { t: 2.8, value: [0, 0, 0], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0.02, 0, 0], ease: "sine" },
            { t: 1.4, value: [-0.03, 0, 0], ease: "sine" },
            { t: 2.8, value: [0.02, 0, 0], ease: "sine" },
          ],
        },
        {
          // The ears flop half a beat behind the bounce.
          target: "ear-l", pivot: EAR_L_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.9, value: [0.08, 0, 0.1], ease: "sine" },
            { t: 1.6, value: [-0.04, 0, -0.02], ease: "sine" },
            { t: 2.3, value: [0.08, 0, 0.1], ease: "sine" },
            { t: 2.8, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "ear-r", pivot: EAR_R_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.9, value: [0.08, 0, -0.1], ease: "sine" },
            { t: 1.6, value: [-0.04, 0, 0.02], ease: "sine" },
            { t: 2.3, value: [0.08, 0, -0.1], ease: "sine" },
            { t: 2.8, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          // The joey rides the bounce with mum.
          target: "joey", pivot: POUCH_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.7, value: [0, -0.022, 0], ease: "sine" },
            { t: 1.4, value: [0, 0.012, 0], ease: "sine" },
            { t: 2.1, value: [0, -0.022, 0], ease: "sine" },
            { t: 2.8, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },

    // 2. Joey peek — the joey pops up out of the pouch, looks left and right,
    //    then ducks down out of sight and rises shyly back; mum tips her head
    //    down to check on it.
    {
      id: "joey-peek",
      label: "Joey peek",
      duration: 4.6,
      loop: true,
      tracks: [
        {
          target: "joey", pivot: POUCH_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.6, value: [0, 0.05, 0.01], ease: "easeOut" },
            { t: 2.4, value: [0, 0.05, 0.01], ease: "easeInOut" },
            { t: 3.0, value: [0, -0.085, -0.02], ease: "easeIn" },
            { t: 3.8, value: [0, -0.085, -0.02], ease: "easeInOut" },
            { t: 4.6, value: [0, 0, 0], ease: "easeOut" },
          ],
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 1.0, value: [0, 0.45, 0], ease: "easeInOut" },
            { t: 1.8, value: [0, -0.45, 0], ease: "easeInOut" },
            { t: 2.4, value: [0, 0, 0], ease: "easeInOut" },
            { t: 4.6, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          // Mum tips her head down toward the pouch as the joey ducks.
          target: "head", pivot: NECK_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 2.4, value: [0.06, 0, 0], ease: "easeInOut" },
            { t: 3.2, value: [0.3, 0, 0.05], ease: "easeInOut" },
            { t: 3.8, value: [0.3, 0, 0.05], ease: "easeInOut" },
            { t: 4.6, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          // Her ears perk forward while she watches.
          target: "ear-l", pivot: EAR_L_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 3.2, value: [0.18, 0, 0.05], ease: "easeInOut" },
            { t: 3.8, value: [0.18, 0, 0.05], ease: "easeInOut" },
            { t: 4.6, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          target: "ear-r", pivot: EAR_R_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeInOut" },
            { t: 3.2, value: [0.18, 0, -0.05], ease: "easeInOut" },
            { t: 3.8, value: [0.18, 0, -0.05], ease: "easeInOut" },
            { t: 4.6, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
      ],
    },
  ],
});
